import React, { Dispatch, forwardRef, useEffect, useState } from 'react';
import { useSideDrawer } from '../../HBSideDrawer/HBSideDrawer';
import * as Popover from '@radix-ui/react-popover';
import SideNavMenuItem from './SideNavMenuItem';
import { useDebouncedCallback } from '../../../hooks/useDebouncedCallback';
import { ISideNavItem } from '../HBSideNav';
import { useSideNavContext } from '../context/SideNavProvider';
import HBSideNavMenu from '../SideNavMenu/HBSideNavMenu';
import styled from 'styled-components';

export interface IMenuItemContainer {
    isOpen?: boolean;
    menuItem: ISideNavItem;
    isSelected?: boolean;
    parentLabel?: ISideNavItem['label'];
    selectedSubItem?: ISideNavItem['label'];
    showChevron?: boolean;
    onMenuItemClick?: (
        e?: React.MouseEvent<HTMLElement, MouseEvent>,
        details?: {
            item: ISideNavItem;
            parentLabel?: ISideNavItem['label'];
            source?: 'sidenav' | 'popover';
        }
    ) => void;
    width?: number;
    persistentItemOpen?: ISideNavItem;
    setPersistentItemOpen?: Dispatch<ISideNavItem>;
    source?: 'sidenav' | 'popover';
}

const SideNavMenuItemContainer = forwardRef(
    (
        {
            menuItem,
            isOpen,
            onMenuItemClick,
            isSelected,
            parentLabel,
            selectedSubItem,
            showChevron,
            persistentItemOpen,
            setPersistentItemOpen,
            source
        }: IMenuItemContainer,
        ref: React.Ref<HTMLAnchorElement>
    ) => {
        const { isExpanded, isMinimized } = useSideDrawer();
        const { theme, supressPopovers } = useSideNavContext();

        const [openPopover, setOpenPopover] = useState(false);
        const debouncePopover = useDebouncedCallback(
            val => setOpenPopover(val),
            300
        );

        const handleHoverEnter = () => {
            if (menuItem.openOnClick || persistentItemOpen) {
                return;
            }
            debouncePopover(true);
        };

        const handleHoverLeave = () => {
            if (menuItem.openOnClick || persistentItemOpen) {
                return;
            }

            debouncePopover(false);
        };

        const handlePopoverClick = () => {
            if (menuItem.openOnClick) {
                setPersistentItemOpen(menuItem);
                debouncePopover(true);
            }
        };

        const handleItemClick: IMenuItemContainer['onMenuItemClick'] = (
            e,
            details
        ) => {
            const { externalUrl, preventCloseOnClick } = details.item;
            e.preventDefault();

            if (!externalUrl && !e.metaKey) {
                onMenuItemClick?.(e, details);
            }
            menuItem?.onClick?.(e, { ...details, source });

            if (preventCloseOnClick) {
                setOpenPopover(true);
                return;
            }

            setOpenPopover(false);
        };

        const isPopoverOpen = openPopover && !isOpen && isExpanded;

        useEffect(() => {
            if (isSelected && !!menuItem.openOnClick && !isExpanded) {
                debouncePopover(false);
                setPersistentItemOpen?.(null);
                onMenuItemClick?.(undefined, null);
            }
        }, [
            isExpanded,
            debouncePopover,
            setPersistentItemOpen,
            onMenuItemClick,
            menuItem.openOnClick,
            isSelected
        ]);

        if ((!menuItem.subItems || supressPopovers) && !menuItem.openOnClick) {
            return (
                <SideNavMenuItem
                    {...menuItem}
                    ref={ref}
                    theme={theme}
                    parentLabel={parentLabel}
                    hideLabel={isMinimized}
                    isOpen={isOpen}
                    showChevron={showChevron}
                    isSelected={isSelected}
                    onClick={e => {
                        handleItemClick(e, {
                            item: menuItem,
                            parentLabel
                        });
                    }}
                    persistentItemOpen={persistentItemOpen}
                    testIdName={menuItem.testIdName}
                />
            );
        }

        return (
            <Popover.Root open={isPopoverOpen} onOpenChange={debouncePopover}>
                <Popover.Anchor
                    asChild
                    onMouseEnter={handleHoverEnter}
                    onMouseLeave={handleHoverLeave}
                    onClick={handlePopoverClick}
                >
                    <SideNavMenuItem
                        {...menuItem}
                        ref={ref}
                        theme={theme}
                        parentLabel={parentLabel}
                        hideLabel={isMinimized}
                        isOpen={isOpen}
                        showChevron={showChevron}
                        isSelected={isSelected}
                        onClick={e => {
                            handleItemClick(e, {
                                item: menuItem,
                                parentLabel
                            });
                        }}
                        persistentItemOpen={persistentItemOpen}
                        testIdName={menuItem.testIdName}
                    />
                </Popover.Anchor>
                <Popover.Content
                    style={{
                        zIndex: -1
                    }}
                    asChild
                    side="right"
                    sideOffset={10}
                    collisionPadding={2}
                    onMouseEnter={handleHoverEnter}
                    onMouseLeave={handleHoverLeave}
                    onCloseAutoFocus={e => e.preventDefault()}
                    onInteractOutside={e => {
                        if (menuItem.openOnClick) {
                            setPersistentItemOpen(undefined);
                            debouncePopover(false);
                        }
                        e.preventDefault();
                    }}
                >
                    {isExpanded && menuItem?.subItems?.length !== 0 && (
                        <StyledBridgeContainer data-testid="popover">
                            {menuItem?.renderPopover ? (
                                menuItem.renderPopover({
                                    theme,
                                    menuItem,
                                    onMenuItemClick: handleItemClick,
                                    source: 'popover'
                                })
                            ) : (
                                <HBSideNavMenu theme={theme}>
                                    {menuItem.subItems?.map(
                                        ({ label, ...subItem }, index) => (
                                            <SideNavMenuItemContainer
                                                key={index}
                                                menuItem={{
                                                    label,
                                                    ...subItem
                                                }}
                                                showChevron={false}
                                                parentLabel={menuItem.label}
                                                selectedSubItem={
                                                    selectedSubItem
                                                }
                                                isSelected={
                                                    selectedSubItem === label &&
                                                    menuItem.label ===
                                                        selectedSubItem
                                                }
                                                onMenuItemClick={
                                                    handleItemClick
                                                }
                                                source="popover"
                                            />
                                        )
                                    )}
                                </HBSideNavMenu>
                            )}
                        </StyledBridgeContainer>
                    )}
                </Popover.Content>
            </Popover.Root>
        );
    }
);

export default SideNavMenuItemContainer;

// -------------------------------- Styles -------------------------------- //

const StyledBridgeContainer = styled.div`
    &::before {
        content: '';
        position: absolute;
        width: 20px;
        height: 100%;
        left: -15px;
        top: 0;
    }
`;
